import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  LogOut,
  Home,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  Circle,
  Plus,
  Target,
  FileText,
  Activity,
  Send,
  ClipboardList
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import AdminSessionManager from './AdminSessionManager';
import { ToolAccessPanel } from '../../components/admin/ToolAccessPanel';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const checklistItems = [
  { key: 'welcome_sent', label: 'Welcome Email Sent' },
  { key: 'intake_sent', label: 'Intake Form Sent' },
  { key: 'intake_completed', label: 'Intake Completed' },
  { key: 'plan_created', label: 'Plan Created' },
  { key: 'kickoff_scheduled', label: 'Kickoff Scheduled' }
];

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [newNote, setNewNote] = useState({ note_type: 'general', content: '' });
  const [newGoal, setNewGoal] = useState({ title: '', description: '', target_date: '', framework_stage: '' });
  const [newTask, setNewTask] = useState({ title: '', description: '', due_date: '', priority: 'normal', goal_id: '' });
  const [newSession, setNewSession] = useState({ title: '', session_date: '', session_time: '', duration: '60 min', session_type: 'one_on_one', notes: '' });

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients/${clientId}`, { withCredentials: true });
      setClientData(response.data);
      // Also fetch sessions
      const sessRes = await axios.get(`${API_URL}/api/admin/clients/${clientId}/sessions`, { withCredentials: true });
      setSessions(sessRes.data);
    } catch (error) {
      console.error('Error fetching client:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChecklistToggle = async (key, currentValue) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/clients/${clientId}/checklist`,
        { [key]: !currentValue },
        { withCredentials: true }
      );
      toast.success('Checklist updated');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };

  const handleSendIntake = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/clients/${clientId}/send-intake`,
        {},
        { withCredentials: true }
      );
      toast.success('Intake form sent!');
      console.log('Intake link:', response.data.intake_link);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to send intake form');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/${clientId}/notes`,
        { client_id: clientId, ...newNote },
        { withCredentials: true }
      );
      toast.success('Note added');
      setShowAddNote(false);
      setNewNote({ note_type: 'general', content: '' });
      fetchClientData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/${clientId}/goals`,
        { client_id: clientId, ...newGoal },
        { withCredentials: true }
      );
      toast.success('Goal created');
      setShowAddGoal(false);
      setNewGoal({ title: '', description: '', target_date: '', framework_stage: '' });
      fetchClientData();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/${clientId}/tasks`,
        { client_id: clientId, ...newTask },
        { withCredentials: true }
      );
      toast.success('Task created');
      setShowAddTask(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'normal', goal_id: '' });
      fetchClientData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleTaskStatusToggle = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await axios.patch(
        `${API_URL}/api/admin/tasks/${taskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Task ${newStatus}`);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/${clientId}/sessions`,
        newSession,
        { withCredentials: true }
      );
      toast.success('Session scheduled');
      setShowAddSession(false);
      setNewSession({ title: '', session_date: '', session_time: '', duration: '60 min', session_type: 'one_on_one', notes: '', zoom_url: '' });
      fetchClientData();
    } catch (error) {
      toast.error('Failed to schedule session');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!clientData?.client) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-[#0A0F1C] mb-4">Client not found</h1>
          <Link to="/admin/clients" className="text-[#1D4ED8] hover:underline">Back to Clients</Link>
        </div>
      </div>
    );
  }

  const { client, intake, activities, goals, tasks, notes } = clientData;

  return (
    <AdminLayout title="Client Detail"><div>
        <Link to="/admin/clients" className="inline-flex items-center gap-2 text-[#475569] hover:text-[#1D4ED8] mb-6">
          <ArrowLeft size={18} />
          Back to Clients
        </Link>

        {/* Client Header */}
        <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#0A0F1C]" data-testid="client-name">{client.name}</h1>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-[#475569] flex items-center gap-1">
                  <Mail size={14} /> {client.email}
                </span>
                {client.phone && (
                  <span className="text-sm text-[#475569] flex items-center gap-1">
                    <Phone size={14} /> {client.phone}
                  </span>
                )}
              </div>
              {client.service_type && (
                <p className="text-sm text-[#475569] mt-1">
                  <span className="font-medium">Service:</span> {client.service_type}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm px-3 py-1 rounded-full ${
                client.onboarding_status === 'active' ? 'bg-green-100 text-green-700' :
                client.onboarding_status === 'intake_pending' ? 'bg-yellow-100 text-yellow-700' :
                'bg-[#EFF6FF] text-[#1D4ED8]'
              }`}>
                {client.onboarding_status?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['overview', 'intake', 'goals', 'tasks', 'notes', 'sessions', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-[#1D4ED8] text-white'
                  : 'bg-white text-[#475569] hover:bg-[#F1F5F9]'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <h2 className="text-lg font-bold text-[#0A0F1C] mb-4">Onboarding Checklist</h2>
                <div className="space-y-3">
                  {checklistItems.map((item) => {
                    const isCompleted = client.onboarding_checklist?.[item.key];
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleChecklistToggle(item.key, isCompleted)}
                        className="w-full flex items-center gap-3 p-3 border border-[#E2E8F0] rounded hover:bg-[#F9FAFB] transition-colors text-left"
                        data-testid={`checklist-${item.key}`}
                      >
                        {isCompleted ? (
                          <CheckCircle size={20} className="text-green-600" />
                        ) : (
                          <Circle size={20} className="text-[#94A3B8]" />
                        )}
                        <span className={isCompleted ? 'text-[#475569] line-through' : 'text-[#0A0F1C]'}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {!client.onboarding_checklist?.intake_sent && (
                  <Button
                    onClick={handleSendIntake}
                    className="btn-primary-orange mt-4 flex items-center gap-2"
                    data-testid="send-intake-btn"
                  >
                    <Send size={16} />
                    Send Intake Form
                  </Button>
                )}
              </div>
            )}

            {activeTab === 'intake' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <h2 className="text-lg font-bold text-[#0A0F1C] mb-4">Intake Response</h2>
                {intake ? (
                  <div className="space-y-4">
                    {intake.business_name && (
                      <div>
                        <span className="text-xs text-[#94A3B8] uppercase">Business Name</span>
                        <p className="text-[#0A0F1C]">{intake.business_name}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-[#94A3B8] uppercase">Business Stage</span>
                      <p className="text-[#0A0F1C] capitalize">{intake.business_stage}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[#94A3B8] uppercase">Biggest Challenge</span>
                      <p className="text-[#0A0F1C]">{intake.biggest_challenge}</p>
                    </div>
                    {intake.goals && intake.goals.length > 0 && (
                      <div>
                        <span className="text-xs text-[#94A3B8] uppercase">Goals</span>
                        <ul className="list-disc list-inside text-[#0A0F1C]">
                          {intake.goals.map((goal, i) => <li key={i}>{goal}</li>)}
                        </ul>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-[#94A3B8] uppercase">Timeline</span>
                      <p className="text-[#0A0F1C]">{intake.timeline}</p>
                    </div>
                    {intake.budget_range && (
                      <div>
                        <span className="text-xs text-[#94A3B8] uppercase">Budget Range</span>
                        <p className="text-[#0A0F1C]">{intake.budget_range}</p>
                      </div>
                    )}
                    {intake.additional_notes && (
                      <div>
                        <span className="text-xs text-[#94A3B8] uppercase">Additional Notes</span>
                        <p className="text-[#475569] bg-[#F9FAFB] p-3 rounded">{intake.additional_notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ClipboardList size={48} className="text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#475569]">No intake response yet</p>
                    {!client.onboarding_checklist?.intake_sent && (
                      <Button onClick={handleSendIntake} className="btn-primary-blue mt-4">
                        Send Intake Form
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'goals' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0A0F1C]">Goals</h2>
                  <Button onClick={() => setShowAddGoal(true)} className="btn-primary-blue text-sm flex items-center gap-2">
                    <Plus size={16} /> Add Goal
                  </Button>
                </div>
                {goals && goals.length > 0 ? (
                  <div className="space-y-4">
                    {goals.map((goal, index) => (
                      <div key={goal.goal_id || index} className="p-4 border border-[#E2E8F0] rounded" data-testid={`goal-${index}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-[#0A0F1C]">{goal.title}</h3>
                            {goal.description && <p className="text-sm text-[#475569] mt-1">{goal.description}</p>}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            goal.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#F1F5F9] text-[#475569]'
                          }`}>
                            {goal.status}
                          </span>
                        </div>
                        {goal.framework_stage && (
                          <span className="badge-blue text-xs mt-2 inline-block">{goal.framework_stage}</span>
                        )}
                        {goal.target_date && (
                          <p className="text-xs text-[#94A3B8] mt-2 flex items-center gap-1">
                            <Calendar size={12} /> Target: {goal.target_date}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target size={48} className="text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#475569]">No goals yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0A0F1C]">Tasks</h2>
                  <Button onClick={() => setShowAddTask(true)} className="btn-primary-blue text-sm flex items-center gap-2">
                    <Plus size={16} /> Add Task
                  </Button>
                </div>
                {tasks && tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map((task, index) => (
                      <div 
                        key={task.task_id || index} 
                        className="flex items-center gap-3 p-3 border border-[#E2E8F0] rounded"
                        data-testid={`task-${index}`}
                      >
                        <button onClick={() => handleTaskStatusToggle(task.task_id, task.status)}>
                          {task.status === 'completed' ? (
                            <CheckCircle size={20} className="text-green-600" />
                          ) : (
                            <Circle size={20} className="text-[#94A3B8]" />
                          )}
                        </button>
                        <div className="flex-grow">
                          <span className={task.status === 'completed' ? 'text-[#475569] line-through' : 'text-[#0A0F1C]'}>
                            {task.title}
                          </span>
                          {task.due_date && (
                            <span className="text-xs text-[#94A3B8] ml-2">Due: {task.due_date}</span>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          task.priority === 'high' ? 'bg-red-100 text-red-700' :
                          task.priority === 'low' ? 'bg-[#F1F5F9] text-[#475569]' :
                          'bg-[#EFF6FF] text-[#1D4ED8]'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle size={48} className="text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#475569]">No tasks yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0A0F1C]">Notes</h2>
                  <Button onClick={() => setShowAddNote(true)} className="btn-primary-blue text-sm flex items-center gap-2">
                    <Plus size={16} /> Add Note
                  </Button>
                </div>
                {notes && notes.length > 0 ? (
                  <div className="space-y-4">
                    {notes.map((note, index) => (
                      <div key={note.note_id || index} className="p-4 border border-[#E2E8F0] rounded" data-testid={`note-${index}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#94A3B8] uppercase">{note.note_type}</span>
                          <span className="text-xs text-[#94A3B8]">{new Date(note.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-[#475569]">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText size={48} className="text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#475569]">No notes yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sessions' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0A0F1C]">Sessions</h2>
                  <button onClick={() => setShowAddSession(true)} className="text-sm text-[#1D4ED8] font-medium hover:underline" data-testid="add-session-btn">+ Schedule Session</button>
                </div>
                {sessions.length > 0 ? (
                  <div className="space-y-3">
                    {sessions.map((s, i) => (
                      <div key={s.session_id} className="p-4 border border-[#E2E8F0] rounded" data-testid={`session-item-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-[#0A0F1C]">{s.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#EFF6FF] text-[#1D4ED8]'}`}>{s.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-[#475569]">
                          <span>{s.session_date}</span>
                          {s.session_time && <span>{s.session_time}</span>}
                          <span>{s.duration}</span>
                          <span className="capitalize">{s.session_type?.replace(/_/g, ' ')}</span>
                          {s.zoom_url && <span className="text-[#1D4ED8]">Zoom set</span>}
                        </div>
                        <AdminSessionManager
                          session={s}
                          onChange={(updated) => setSessions((prev) => prev.map((x) => x.session_id === updated.session_id ? { ...x, ...updated } : x))}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#475569] text-center py-8">No sessions scheduled</p>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <h2 className="text-lg font-bold text-[#0A0F1C] mb-4">Activity Timeline</h2>
                {activities && activities.length > 0 ? (
                  <div className="space-y-4">
                    {activities.map((activity, index) => (
                      <div key={activity.activity_id || index} className="flex gap-4 pb-4 border-b border-[#F1F5F9] last:border-0">
                        <div className="w-10 h-10 bg-[#EFF6FF] rounded-full flex items-center justify-center flex-shrink-0">
                          <Activity size={18} className="text-[#1D4ED8]" />
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#0A0F1C] text-sm capitalize">
                              {activity.activity_type.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-[#94A3B8]">
                              {new Date(activity.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-[#475569]">{activity.details}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#475569] text-center py-8">No activities yet</p>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ToolAccessPanel clientId={clientId} />
            <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
              <h3 className="font-bold text-[#0A0F1C] mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Goals</span>
                  <span className="font-medium">{goals?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Tasks</span>
                  <span className="font-medium">{tasks?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Completed Tasks</span>
                  <span className="font-medium">{tasks?.filter(t => t.status === 'completed').length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Notes</span>
                  <span className="font-medium">{notes?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Note Modal */}
        {showAddNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Add Note</h3>
              <form onSubmit={handleAddNote} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Type</label>
                  <select
                    value={newNote.note_type}
                    onChange={(e) => setNewNote({ ...newNote, note_type: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="general">General</option>
                    <option value="session">Session Note</option>
                    <option value="milestone">Milestone</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Content</label>
                  <Textarea
                    value={newNote.content}
                    onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                    placeholder="Add note content..."
                    className="form-input min-h-[100px]"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddNote(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Add Note</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Goal Modal */}
        {showAddGoal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Add Goal</h3>
              <form onSubmit={handleAddGoal} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Title</label>
                  <Input
                    value={newGoal.title}
                    onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                    placeholder="Goal title"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Description</label>
                  <Textarea
                    value={newGoal.description}
                    onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                    placeholder="Goal description"
                    className="form-input min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Framework Stage</label>
                  <select
                    value={newGoal.framework_stage}
                    onChange={(e) => setNewGoal({ ...newGoal, framework_stage: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="">Select stage</option>
                    <option value="Foundation">Foundation</option>
                    <option value="Validation">Validation</option>
                    <option value="Credibility">Credibility</option>
                    <option value="Brand">Brand</option>
                    <option value="Systems">Systems</option>
                    <option value="Protection">Protection</option>
                    <option value="Launch">Launch</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Target Date</label>
                  <Input
                    type="date"
                    value={newGoal.target_date}
                    onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddGoal(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create Goal</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Task Modal */}
        {showAddTask && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Add Task</h3>
              <form onSubmit={handleAddTask} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Title</label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Task title"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Description</label>
                  <Textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Task description"
                    className="form-input min-h-[60px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Due Date</label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                      className="form-input w-full"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                {goals && goals.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Related Goal</label>
                    <select
                      value={newTask.goal_id}
                      onChange={(e) => setNewTask({ ...newTask, goal_id: e.target.value })}
                      className="form-input w-full"
                    >
                      <option value="">No related goal</option>
                      {goals.map(g => (
                        <option key={g.goal_id} value={g.goal_id}>{g.title}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddTask(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create Task</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Session Modal */}
        {showAddSession && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Schedule Session</h3>
              <form onSubmit={handleCreateSession} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Title</label>
                  <Input
                    value={newSession.title}
                    onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                    placeholder="Consulting Session"
                    required
                    data-testid="session-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Date</label>
                    <Input type="date" value={newSession.session_date} onChange={(e) => setNewSession({...newSession, session_date: e.target.value})} required data-testid="session-date-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Time</label>
                    <Input value={newSession.session_time} onChange={(e) => setNewSession({...newSession, session_time: e.target.value})} placeholder="7:00 PM" data-testid="session-time-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Duration</label>
                    <select value={newSession.duration} onChange={(e) => setNewSession({...newSession, duration: e.target.value})} className="form-input w-full">
                      <option value="30 min">30 min</option>
                      <option value="60 min">60 min</option>
                      <option value="90 min">90 min</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Type</label>
                    <select value={newSession.session_type} onChange={(e) => setNewSession({...newSession, session_type: e.target.value})} className="form-input w-full">
                      <option value="one_on_one">1-on-1</option>
                      <option value="group">Group</option>
                      <option value="review">Review</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Zoom link <span className="text-[#94A3B8] font-normal">(optional — blank uses native room)</span></label>
                  <Input value={newSession.zoom_url} onChange={(e) => setNewSession({...newSession, zoom_url: e.target.value})} placeholder="https://zoom.us/j/..." data-testid="session-zoom-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Notes</label>
                  <Textarea value={newSession.notes} onChange={(e) => setNewSession({...newSession, notes: e.target.value})} placeholder="Session notes or agenda..." />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddSession(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Schedule</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div></AdminLayout>);}
